import { theme } from '@Guidelines/Theme/theme';
import { ClassNames, css } from '@emotion/react';
import type * as React from 'react';
import { Button, Dialog } from '@verclam/ui-kit';

const panelStyle = css`
	width: 36.25rem;
	max-width: 100%;
	border-radius: 26px;

	@media (max-width: ${theme.layout.compactBreakpoint}) {
		width: 100%;
		border-radius: 26px 26px 0 0;
	}
`;

const containerStyle = css`
	@media (max-width: ${theme.layout.compactBreakpoint}) {
		align-items: flex-end;
		padding: 0;
	}
`;

const subtitleStyle = css`
	margin: 0 0 1rem;
	font-family: ${theme.fonts.body};
	font-size: 0.84375rem;
	line-height: 1.6;
	color: ${theme.colors.inkSecondary};
`;

const footerStyle = css`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.625rem;
`;

type Props = {
	readonly visible: boolean;
	readonly onHide: () => void;
	/** When given, a « ← Retour » button sits opposite « Fermer ». */
	readonly onBack?: () => void;
	readonly header: string;
	readonly subtitle: string;
	readonly children: React.ReactNode;
};

/**
 * Sheet that carries a secondary task of the preparation: a centered panel on wide screens, a panel
 * docked to the bottom edge below the compact breakpoint.
 */
const PreparationSheet: React.FC<Props> = ({ visible, onHide, onBack, header, subtitle, children }) => {
	return (
		<ClassNames>
			{({ css: toClassName }) => (
				<Dialog
					blockScroll
					dismissableMask
					draggable={false}
					resizable={false}
					visible={visible}
					onHide={onHide}
					header={header}
					className={toClassName(panelStyle)}
					pt={{ container: { className: toClassName(containerStyle) } }}
					footer={
						<div css={footerStyle}>
							{onBack !== undefined ? (
								<Button
									type="button"
									severity="secondary"
									outlined
									label="← Retour"
									onClick={onBack}
								/>
							) : (
								<span />
							)}
							<Button type="button" severity="secondary" label="Fermer" onClick={onHide} />
						</div>
					}
				>
					<p css={subtitleStyle}>{subtitle}</p>
					{children}
				</Dialog>
			)}
		</ClassNames>
	);
};

export default PreparationSheet;
